import { useMemo } from 'react'

import { faChevronDown } from '@fortawesome/pro-light-svg-icons/faChevronDown'
import { useDecision } from '@optimizely/react-sdk'
import { toastEventDispatch } from '@space-lib-events/toast'
import { ToastType, ToastPosition } from '@space-lib-events/toast/enum'
import CustomDropdown from '@space-lib/components/dropdown/customDropdown'
import Table from '@space-lib/components/table'
import Tooltip from '@space-lib/components/tooltip'
import {
  Entities,
  Actions,
  EventNames,
  ScreenNames,
  ComponentNames,
  RequestResults
} from '@space-lib/datahub'

import useTranslation from '@space-lib/hooks/useTranslation'
import type { CommunityMemberRoles } from '@space-shell/common/services/communities'
import useCommunityConfigurationSidebar from '@space-shell/modules/communities/communityConfigurationSidebar/hooks/useConfigurationCommunitySidebar/useCommunityConfigurationSidebar'
import { useEditFollowerRole } from '@space-shell/modules/communities/hooks'

import useSendDataHubConsume from '@space-shell/modules/communities/hooks/useSendDataHubConsume/useSendDataHubConsume'

import CustomSpinner from './customSpinner'

import type { RoleActionProps } from './types'

import { DisplayBtn } from '../styles'

const RoleAction = ({
  communityId,
  member,
  options,
  visibleOptions,
  isTheOwner,
  canEdit,
  isLoadingCommunityData,
  refetchGetFollowersRoles
}: RoleActionProps) => {
  const { t } = useTranslation()
  const { sendDataHubConsume } = useSendDataHubConsume()
  const { editFollowerRoleQuery } = useEditFollowerRole()
  const {
    community: { isLocked }
  } = useCommunityConfigurationSidebar()

  const [{ enabled: isEnabledChangeCommunityType }] = useDecision(
    'com_adm_web_enable_change_community_type'
  )

  const isCommunityLocked = isEnabledChangeCommunityType && isLocked

  const selected = useMemo(
    () => visibleOptions.find((opt) => opt.value === member.memberType),
    [member.memberType, visibleOptions]
  )

  const currentMemberOpt = useMemo(
    () => options.find((opt) => opt.value === member.memberType)?.label,
    [options, member.memberType]
  )

  const editFollowerRole = (memberType: CommunityMemberRoles) => {
    if (memberType === member.memberType) return

    editFollowerRoleQuery.mutate(
      { body: { memberType }, communityId, userId: member.id },
      {
        onSuccess: (_, variables) => {
          toastEventDispatch({
            type: ToastType.SUCCESS,
            message: t(
              'configuration_sidebar.members_and_permissions.messages.manage_role_success',
              {
                user: member.name,
                memberType: visibleOptions.find((opt) => opt.value === variables.body.memberType)
                  ?.label
              }
            ),
            position: ToastPosition.BOTTOM_RIGHT
          })
          sendDataHubConsume({
            entity: Entities.MEMBERS_AND_PERMISSIONS,
            action: Actions.UPDATE_PERMISSION,
            event: {
              eventName: EventNames.COMMUNITY_CHANGE_USER_PERMISSION,
              screenName: ScreenNames.COMMUNITY_EDIT_VIEW,
              componentName: ComponentNames.CONFIGURATION_MODAL,
              communityMemberRole: variables.body.memberType
            }
          })
          refetchGetFollowersRoles()
        },
        onError: (e, variables) => {
          toastEventDispatch({
            type: ToastType.DANGER,
            message: t('configuration_sidebar.members_and_permissions.messages.manage_role_error', {
              user: member.name,
              memberType: visibleOptions.find((opt) => opt.value === variables.body.memberType)
                ?.label
            }),
            position: ToastPosition.BOTTOM_RIGHT
          })

          sendDataHubConsume({
            entity: Entities.MEMBERS_AND_PERMISSIONS,
            action: Actions.UPDATE_PERMISSION,
            event: {
              eventName: EventNames.COMMUNITY_CHANGE_USER_PERMISSION,
              screenName: ScreenNames.COMMUNITY_EDIT_VIEW,
              componentName: ComponentNames.CONFIGURATION_MODAL,
              communityMemberRole: variables.body.memberType,
              result: RequestResults.FAILURE,
              failureKind: e?.message
            }
          })
        }
      }
    )
  }

  if (!canEdit || isTheOwner) {
    return currentMemberOpt ? (
      <Table.Data className='_p-0 _align-middle _text-center'>
        <DisplayBtn disabled>{currentMemberOpt}</DisplayBtn>
      </Table.Data>
    ) : null
  }

  return (
    <Table.Data className='_p-0 _align-middle _text-center'>
      {isLoadingCommunityData || editFollowerRoleQuery.isLoading ? (
        <CustomSpinner />
      ) : (
        <Tooltip
          content={t(
            `${
              isCommunityLocked ? 'configuration_sidebar.change_community_type_tooltip_alert' : ''
            }`
          )}
          position='top'>
          <CustomDropdown
            className='_d-flex _align-items-center _justify-content-center _flex-nowrap _ml-auto _text-nowrap'
            disabled={isCommunityLocked}
            icon={faChevronDown}
            label={selected?.label}>
            {visibleOptions.map((opt, i) => (
              <CustomDropdown.Item
                disabled={opt.value === member.memberType}
                key={`${opt.value}-${i}`}
                handleClick={() => editFollowerRole(opt.value as CommunityMemberRoles)}>
                <span className='_flex-1 _d-flex _px-5 _py-4'>{opt.label}</span>
              </CustomDropdown.Item>
            ))}
          </CustomDropdown>
        </Tooltip>
      )}
    </Table.Data>
  )
}

export default RoleAction
